import { useCallback, useEffect, useState } from "react";
import { Copy, LinkSimple, Trash, Clock, Check } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectContent, SelectItem, SelectValue,
} from "@/components/ui/select";
import { api, apiError } from "@/lib/api";
import { useLang } from "@/i18n";

const TTL_OPTIONS = [
  { value: 1, key: "shareTtl1h" },
  { value: 24, key: "shareTtl24h" },
  { value: 168, key: "shareTtl7d" },
];

function publicUrl(token) {
  const base = process.env.REACT_APP_BACKEND_URL || "";
  return `${base}/api/public/report/${token}`;
}

function fmtExp(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString(); } catch (_e) { return iso; }
}

export default function ShareLinkDialog({ projectId, projectCode, trigger }) {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [ttl, setTtl] = useState(24);
  const [orient, setOrient] = useState("landscape");
  const [links, setLinks] = useState([]);
  const [creating, setCreating] = useState(false);
  const [copiedId, setCopiedId] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get(`/projects/${projectId}/share-links`);
      setLinks(data || []);
    } catch (_e) { /* silent */ }
  }, [projectId]);

  useEffect(() => { if (open) refresh(); }, [open, refresh]);

  const create = async () => {
    setCreating(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/share-links`, {
        ttl_hours: ttl, orient,
      });
      setLinks((prev) => [data, ...prev]);
      toast.success(t("shareLinkCreated"));
    } catch (e) { toast.error(apiError(e)); }
    finally { setCreating(false); }
  };

  const revoke = async (id) => {
    if (!window.confirm(t("shareLinkRevokeConfirm"))) return;
    try {
      await api.delete(`/share-links/${id}`);
      setLinks((prev) => prev.map((x) => (x.id === id ? { ...x, revoked: true } : x)));
      toast.success(t("shareLinkRevoked"));
    } catch (e) { toast.error(apiError(e)); }
  };

  const copy = async (link) => {
    const url = publicUrl(link.token);
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(link.id);
      setTimeout(() => setCopiedId(null), 2000);
      toast.success(t("shareLinkCopied"));
    } catch (_e) {
      window.prompt(t("shareLinkCopyManual"), url);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent data-testid="share-link-dialog" className="bg-white max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading">
            <LinkSimple size={20} weight="duotone" className="text-blue-900" />
            {t("shareLinkTitle")}
          </DialogTitle>
          <DialogDescription className="text-slate-600">
            {t("shareLinkSubtitle").replace("{code}", projectCode || "")}
          </DialogDescription>
        </DialogHeader>

        <div className="border border-slate-200 rounded-lg p-4 space-y-3 bg-slate-50">
          <p className="text-xs font-bold uppercase tracking-widest text-slate-600">
            {t("shareLinkGenerate")}
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="overline-label mb-1 block">{t("shareLinkTtl")}</label>
              <Select value={String(ttl)} onValueChange={(v) => setTtl(Number(v))}>
                <SelectTrigger data-testid="share-link-ttl-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  {TTL_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={String(o.value)}>{t(o.key)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="overline-label mb-1 block">{t("shareLinkOrient")}</label>
              <Select value={orient} onValueChange={setOrient}>
                <SelectTrigger data-testid="share-link-orient-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="landscape">{t("orientLandscape")}</SelectItem>
                  <SelectItem value="portrait">{t("orientPortrait")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <Button
            data-testid="share-link-create-button"
            onClick={create}
            disabled={creating}
            className="w-full bg-blue-900 hover:bg-blue-800 gap-2"
          >
            <LinkSimple size={16} weight="bold" /> {creating ? t("generating") : t("shareLinkCreate")}
          </Button>
        </div>

        <div className="space-y-2">
          <p className="text-xs font-bold uppercase tracking-widest text-slate-600">
            {t("shareLinkActive")} ({links.filter((l) => !l.revoked && !l.expired).length})
          </p>
          {links.length === 0 ? (
            <p className="text-sm text-slate-500 py-6 text-center" data-testid="share-links-empty">
              {t("shareLinkNone")}
            </p>
          ) : (
            <ul className="divide-y divide-slate-100 border border-slate-200 rounded-lg max-h-64 overflow-y-auto">
              {links.map((l) => {
                const inactive = l.revoked || l.expired;
                return (
                  <li
                    key={l.id}
                    data-testid={`share-link-item-${l.id}`}
                    className={`px-3 py-2 flex items-center gap-3 ${inactive ? "opacity-60 bg-slate-50" : ""}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 text-xs">
                        <Clock size={12} className="text-slate-500" />
                        <span className="text-slate-700">
                          {t("shareLinkExpires")} {fmtExp(l.expires_at)}
                        </span>
                        {l.revoked && <span className="ml-1 text-red-600 font-bold uppercase text-[10px]">{t("shareLinkRevokedTag")}</span>}
                        {l.expired && !l.revoked && <span className="ml-1 text-slate-500 font-bold uppercase text-[10px]">{t("shareLinkExpiredTag")}</span>}
                      </div>
                      <p className="text-[11px] text-slate-500 mt-0.5 truncate font-mono">
                        {publicUrl(l.token)}
                      </p>
                      <p className="text-[10px] text-slate-400 mt-0.5">
                        {t("shareLinkHits").replace("{n}", l.hits ?? 0)} · {l.orient}
                      </p>
                    </div>
                    <Button
                      data-testid={`share-link-copy-${l.id}`}
                      variant="outline"
                      size="sm"
                      disabled={inactive}
                      onClick={() => copy(l)}
                      className="gap-1"
                    >
                      {copiedId === l.id ? <Check size={14} weight="bold" /> : <Copy size={14} weight="bold" />}
                      {copiedId === l.id ? t("copied") : t("copy")}
                    </Button>
                    <Button
                      data-testid={`share-link-revoke-${l.id}`}
                      variant="ghost"
                      size="sm"
                      disabled={inactive}
                      onClick={() => revoke(l.id)}
                      className="text-red-600 hover:bg-red-50 gap-1"
                    >
                      <Trash size={14} weight="bold" />
                    </Button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
